import { useCallback, useEffect, useRef } from "react";
import { useMapContext } from "@evergis/react";
import { Polygon } from "@evergis/sgis/es/features/Polygon";
import { point, lineString, polygon, buffer as turfBuffer, toMercator, toWgs84 } from "@turf/turf";

import { useGeometrySelectionBuffer } from "../../../../../../hooks/geometrySelection/useGeometrySelectionBuffer";
import { useGeometrySelection } from "../../../../../../hooks/geometrySelection/useGeometrySelection";
import { useGeometrySelectionContext } from "../../../../../../hooks/geometrySelection/useGeometrySelectionContext";

import { ShadowedPolySymbol } from "../../../../../../react-sgis/symbols/editorSymbols/ShadowedPolySymbol";
import { GeometrySelectionTypes } from "../../types";
import { SelectionHistoryFeature } from "../../../../../../ducks/geometrySelection/geometrySelection";

// eslint-disable-next-line max-lines-per-function
export const useBufferFeatures = () => {
  const { map } = useMapContext();
  const { bufferLayer } = useGeometrySelectionContext();
  const buffer = useGeometrySelectionBuffer();
  const [{ history, type }] = useGeometrySelection();
  const bufferFeatures = useRef<Polygon[]>([]);

  const getTurfFeature = useCallback(
    (feature: SelectionHistoryFeature) => {
      switch (type) {
        case GeometrySelectionTypes.Point:
          return point(feature._position);
        case GeometrySelectionTypes.Line:
        case GeometrySelectionTypes.Route:
          return lineString(feature._rings[0]);
        default:
          return polygon([...feature._rings.map(rings => [...rings, rings[0]])]);
      }
    },
    [type],
  );

  useEffect(() => {
    if (!history) {
      return;
    }

    if (buffer === undefined || history.step === -1) {
      bufferLayer?.remove(bufferFeatures.current);

      bufferFeatures.current = [];
    } else if (history.step >= 0 && history.features[history.step]?.length) {
      bufferLayer?.remove(bufferFeatures.current);

      bufferFeatures.current = history.features[history.step]?.map(feature => {
        return new Polygon(
          toMercator(turfBuffer(toWgs84(getTurfFeature(feature)), +buffer, { units: "meters" })).geometry.coordinates,
          {
            crs: map.crs,
            symbol: new ShadowedPolySymbol({
              strokeWidth: 2,
              strokeColor: "#e33600",
              shadowSize: 0,
              shadowColor: "rgba(227, 54, 0, 0.3)",
              fillColor: "rgba(228, 57, 4, 0.28)",
              // eslint-disable-next-line @typescript-eslint/no-magic-numbers
              lineDash: [5, 5],
              enclose: true,
            }),
          },
        );
      });

      bufferLayer?.add(bufferFeatures.current);
    }
  }, [buffer, history]); // eslint-disable-line

  useEffect(() => {
    return () => {
      bufferLayer?.remove(bufferFeatures.current);

      bufferFeatures.current = [];
    };
  }, []); // eslint-disable-line
};
